import { useState, useEffect, useRef, useCallback } from 'react';
import { SimulationEngineV2 } from '../simulation/SimulationEngineV2';

/**
 * Custom hook for managing the simulation engine
 * Handles initialization, control, and state updates
 */
export const useSimulationEngine = (config) => {
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentHour, setCurrentHour] = useState(0);
  const [hourlyData, setHourlyData] = useState([]);
  const [stats, setStats] = useState({});
  const [speed, setSpeed] = useState(50);
  const [mode, setMode] = useState('step');
  
  const simulationEngine = useRef(null);
  const animationFrame = useRef(null);
  const lastUpdateTime = useRef(Date.now());
  const cumulativeFlows = useRef({});
  const dataProviderRef = useRef(null);
  
  // Initialize or reinitialize simulation engine when config changes
  // CRITICAL: Must recreate engine when config changes (e.g., heater_MW, battery_MW)
  useEffect(() => {
    if (config) {
      // Always recreate engine with new config to ensure constraints are updated
      simulationEngine.current = new SimulationEngineV2(config);
      console.log('🔧 SimulationEngine (re)initialized with config:', {
        heater_MW: config.heater_MW,
        battery_MW: config.battery_MW,
        turbine_MW: config.turbine_MW,
        annualDemandGWh: config.annualDemandGWh,
      });
      // Set data provider if available
      if (dataProviderRef.current) {
        simulationEngine.current.setDataProvider(dataProviderRef.current);
      }
    }
  }, [config]);
  
  // Function to set data provider
  const setDataProvider = useCallback((provider) => {
    console.log('🔄 Setting data provider:', provider);
    dataProviderRef.current = provider;
    if (simulationEngine.current) {
      console.log('✅ Data provider set on simulation engine');
      simulationEngine.current.setDataProvider(provider);
    } else {
      console.warn('⚠️ Simulation engine not initialized yet');
    }
  }, []);
  
  // Handle simulation completion
  const handleSimulationComplete = useCallback((dataToValidate) => {
    setIsRunning(false);
    setIsPaused(false);
    
    const finalStats = simulationEngine.current.getFinalStats();
    setStats(finalStats);
    
    // Validation removed - old validator belongs to legacy version
    
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
  }, [hourlyData]);
  
  // Run simulation step
  const runSimulationStep = useCallback(() => {
    console.log('🎮 runSimulationStep called - isRunning:', isRunning, 'isPaused:', isPaused);
    
    if (!simulationEngine.current) {
      console.error('❌ No simulation engine available');
      return;
    }
    
    if (!isRunning || isPaused) {
      console.log('⏸️ Simulation not running or paused');
      return;
    }
    
    // Check if we have data provider
    if (!dataProviderRef.current) {
      console.error('❌ No data provider available');
      return;
    }
    
    console.log('✅ All checks passed, proceeding with simulation step');
    
    const now = Date.now();
    const elapsed = now - lastUpdateTime.current;
    const delay = mode === 'instant' ? 0 : Math.max(10, 1000 - (speed * 10));
    
    if (elapsed < delay && mode !== 'instant') {
      animationFrame.current = requestAnimationFrame(runSimulationStep);
      return;
    }
    
    lastUpdateTime.current = now;
    
    // Get current hour data from provider
    const currentHour = simulationEngine.current.currentHour;
    console.log(`📊 Getting data for hour ${currentHour}`);
    
    const hourData = {
      spotPrice: dataProviderRef.current.getSpotPrice(currentHour),
      solarMWh: dataProviderRef.current.getSolarMW(currentHour),
      wasteHeatMW: dataProviderRef.current.getWasteHeatMW(currentHour),
      demandMW: dataProviderRef.current.getDemandMW(currentHour),
      hour: currentHour % 24,
      day: Math.floor(currentHour / 24) + 1,
      month: Math.floor(currentHour / (24 * 30)) + 1
    };
    
    console.log('📈 Hour data:', hourData);
    
    const result = simulationEngine.current.simulateHour(hourData);
    console.log('🎯 Simulation result:', result);
    
    if (result) {
      setCurrentHour(result.hour);
      setHourlyData(prev => [...prev, result]);
      
      // Update cumulative flows
      if (result.actions) {
        result.actions.forEach(action => {
          const key = `${action.source}->${action.target}`;
          cumulativeFlows.current[key] = (cumulativeFlows.current[key] || 0) + action.amount;
        });
      }
      
      // Update stats every 100 hours
      if (result.hour % 100 === 0 || result.hour === 8783) {
        const newStats = simulationEngine.current.getFinalStats();
        setStats(newStats);
      }
      
      if (result.hour >= 8783) {
        handleSimulationComplete();
      } else {
        animationFrame.current = requestAnimationFrame(runSimulationStep);
      }
    }
  }, [isRunning, isPaused, speed, mode, handleSimulationComplete]);
  
  // Control functions
  const startSimulation = useCallback(() => {
    console.log('▶️ Starting simulation');
    console.log('Current engine:', simulationEngine.current);
    console.log('Current dataProvider:', dataProviderRef.current);

    if (!simulationEngine.current) {
      console.error('❌ Cannot start: No simulation engine');
      return;
    }

    if (!dataProviderRef.current) {
      console.error('❌ Cannot start: No data provider');
      return;
    }

    setIsRunning(true);
    setIsPaused(false);

    // Force instant mode path for offline dataset
    if (mode !== 'instant') {
      setMode('instant');
    }

    // Run simulation in CHUNKS to avoid blocking UI thread
    console.log('🚀 Starting chunked async simulation');
    const allResults = [];
    const startTime = Date.now();
    const CHUNK_SIZE = 200; // Process 200 hours per chunk

    const processChunk = () => {
      const chunkStart = simulationEngine.current.currentHour;
      const chunkEnd = Math.min(chunkStart + CHUNK_SIZE, 8783);

      // Process one chunk
      for (let i = chunkStart; i < chunkEnd; i++) {
        const hourData = {
          spotPrice: dataProviderRef.current.getSpotPrice(i),
          solarMWh: dataProviderRef.current.getSolarMW(i),
          wasteHeatMW: dataProviderRef.current.getWasteHeatMW(i),
          demandMW: dataProviderRef.current.getDemandMW(i),
          hour: i % 24,
          day: Math.floor(i / 24) + 1,
          month: Math.floor(i / (24 * 30)) + 1
        };

        const result = simulationEngine.current.simulateHour(hourData);
        if (result) {
          allResults.push(result);
          if (result.actions) {
            result.actions.forEach(action => {
              const key = `${action.source}->${action.target}`;
              cumulativeFlows.current[key] = (cumulativeFlows.current[key] || 0) + action.amount;
            });
          }
        }
      }

      // Update progress (this will cause re-render with updated hour count)
      setCurrentHour(simulationEngine.current.currentHour);

      // Check if done
      if (simulationEngine.current.currentHour >= 8783) {
        const duration = ((Date.now() - startTime) / 1000).toFixed(1);
        console.log(`✅ Simulation complete in ${duration}s (${allResults.length} hours)`);

        setHourlyData(allResults);
        const finalStats = simulationEngine.current.getFinalStats();
        setStats(finalStats);
        handleSimulationComplete(allResults);
      } else {
        // Schedule next chunk - setTimeout(0) yields to browser event loop
        setTimeout(processChunk, 0);
      }
    };

    // Start first chunk after 50ms to show loading indicator
    setTimeout(processChunk, 50);
  }, [mode, handleSimulationComplete]);
  
  const pauseSimulation = useCallback(() => {
    setIsPaused(true);
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
  }, []);
  
  const resumeSimulation = useCallback(() => {
    setIsPaused(false);
    animationFrame.current = requestAnimationFrame(runSimulationStep);
  }, [runSimulationStep]);
  
  const resetSimulation = useCallback(() => {
    setIsRunning(false);
    setIsPaused(false);
    setCurrentHour(0);
    setHourlyData([]);
    setStats({});
    cumulativeFlows.current = {};
    
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
    
    // Re-create the simulation engine to reset state
    if (simulationEngine.current) {
      simulationEngine.current = new SimulationEngineV2(simulationEngine.current.config);
      // Restore data provider after reset
      if (dataProviderRef.current) {
        simulationEngine.current.setDataProvider(dataProviderRef.current);
      }
    }
  }, []);
  
  const jumpToHour = useCallback((hour) => {
    if (hour < 0 || hour > 8783) return;
    if (!dataProviderRef.current) {
      console.error('No data provider available for jumpToHour');
      return;
    }
    
    resetSimulation();
    
    // Run simulation up to target hour in instant mode
    const previousMode = mode;
    setMode('instant');
    
    while (simulationEngine.current.currentHour < hour) {
      const currentHour = simulationEngine.current.currentHour;
      const hourData = {
        spotPrice: dataProviderRef.current.getSpotPrice(currentHour),
        solarMWh: dataProviderRef.current.getSolarMW(currentHour),
        wasteHeatMW: dataProviderRef.current.getWasteHeatMW(currentHour),
        demandMW: dataProviderRef.current.getDemandMW(currentHour),
        hour: currentHour % 24,
        day: Math.floor(currentHour / 24) + 1,
        month: Math.floor(currentHour / (24 * 30)) + 1
      };
      
      const result = simulationEngine.current.simulateHour(hourData);
      if (result) {
        setHourlyData(prev => [...prev, result]);
        setCurrentHour(result.hour);
      }
    }
    
    setMode(previousMode);
    const newStats = simulationEngine.current.getFinalStats();
    setStats(newStats);
  }, [mode, resetSimulation]);
  
  // Cleanup
  useEffect(() => {
    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, []);
  
  return {
    // State
    isRunning,
    isPaused,
    currentHour,
    hourlyData,
    stats,
    speed,
    mode,
    cumulativeFlows: cumulativeFlows.current,
    
    // Controls
    startSimulation,
    pauseSimulation,
    resumeSimulation,
    resetSimulation,
    jumpToHour,
    setSpeed,
    setMode,
    setDataProvider,
    
    // Engine reference
    engine: simulationEngine.current,
  };
};